<script setup lang="ts">
import type { DrawingMode } from '~/composables/useMapDrawing';

import { cn } from '@/utils';

import { Button } from '~/components/ui/button';

const props = defineProps<{
	/**
	 * Whether drawing mode is currently active
	 */
	isDrawingMode: boolean;
	/**
	 * Current drawing mode
	 */
	currentMode?: DrawingMode;
	/**
	 * Additional classes for the button container
	 */
	class?: string;
}>();

const emit = defineEmits<{
	/**
	 * Emitted when user clicks to toggle drawing mode with specified mode
	 */
	toggle: [mode: DrawingMode];
}>();

const activeMode = computed(() => props.currentMode || 'freehand');

const isFreehandActive = computed(
	() => props.isDrawingMode && activeMode.value === 'freehand'
);

const isRectangleActive = computed(
	() => props.isDrawingMode && activeMode.value === 'rectangle'
);

function handleFreehandClick() {
	emit('toggle', 'freehand');
}

function handleRectangleClick() {
	emit('toggle', 'rectangle');
}
</script>

<template>
	<div :class="cn('flex shrink-0 items-center gap-2', props.class)">
		<Button
			:variant="isFreehandActive ? 'primary' : 'white-shadow'"
			size="sm"
			class="flex h-11 min-w-11 items-center gap-2 px-3"
			aria-label="Seleção livre: alternar no mapa"
			:aria-pressed="isFreehandActive"
			title="Seleção livre"
			type="button"
			@click="handleFreehandClick"
		>
			<svg
				aria-hidden="true"
				class="size-5"
				:class="isFreehandActive ? 'text-white' : 'text-gray-700'"
				viewBox="0 -960 960 960"
			>
				<path
					fill="currentColor"
					d="M561-120q-52 0-86-36t-34-93q0-76 66-133.5T669-448q-3-55-23.5-81.5T584-556q-42 0-76 26t-88 100q-60 82-99.5 112.5T234-287q-53 0-83.5-34.5T120-416q0-32 23-83t77-140q26-39 37.5-64t11.5-42q0-11-7.5-17.5T240-769q-16 0-34 11.5T168-723l-48-51q30-34 59-50t60-16q43 0 72 28t29 70q0 30-14.5 61T263-575q-45 69-58.5 100.5T191-411q0 22 15 37.5t36 15.5q14 0 27.5-7t29.5-24q13-14 29.5-34t39.5-50q66-86 115.5-120T593-627q63 0 102.5 47.5T740-451h100v71H740q-8 120-56 190t-123 70Zm3-71q43 0 73-52t34-135q-66 7-112.5 47T512-242q0 24 13.5 37.5T564-191Z"
				/>
			</svg>
			<span
				class="hidden lg:inline"
				:class="isFreehandActive ? 'text-white' : 'text-gray-700'"
			>
				Seleção livre
			</span>
		</Button>

		<Button
			:variant="isRectangleActive ? 'primary' : 'white-shadow'"
			size="sm"
			class="flex h-11 min-w-11 items-center gap-2 px-3"
			aria-label="Retângulo: alternar seleção no mapa"
			:aria-pressed="isRectangleActive"
			title="Seleção retangular"
			type="button"
			@click="handleRectangleClick"
		>
			<svg
				aria-hidden="true"
				class="size-5"
				:class="isRectangleActive ? 'text-white' : 'text-gray-700'"
				viewBox="0 -960 960 960"
			>
				<path
					fill="currentColor"
					d="M80-160v-640h800v640H80Zm60-60h680v-520H140v520Zm0 0v-520 520Z"
				/>
			</svg>
			<span
				class="hidden lg:inline"
				:class="isRectangleActive ? 'text-white' : 'text-gray-700'"
			>
				Retângulo
			</span>
		</Button>
	</div>
</template>
